import { useMemo } from 'react';
import { useFetchData } from '../hooks/useFetchData';
import { enhanceHomeResponse } from './demoDataService';
import { useStytchMemberSession } from '@stytch/react/b2b';
import { useEnvironment } from '../contexts/EnvironmentContext';

// ── normalizers ────────────────────────────────────────────────────────────

function normalizeV2Service(svc) {
  return {
    app_id: String(svc.service_id),
    app_name: svc.service_name,
    kpis: {
      activity: { total_traces: svc.summary?.trace_count ?? null, total_sessions: svc.summary?.session_count ?? null },
      detections: { errors: svc.summary?.classifier_error_total ?? null },
      latency: { p95_total: svc.summary?.p95_ms ?? null },
      cost: { total_cost_usd: svc.summary?.cost_usd ?? null },
      classifiers: { negative: { total: svc.summary?.classifiers_negative_total ?? null } },
    },
  };
}

// ── hook ───────────────────────────────────────────────────────────────────

/**
 * Fetch the list of apps/services for the Home page.
 *
 * V2: GET /v2/data/services?env=...&range=...&summary=true
 *     Returns service list with range-scoped KPI summary block.
 * useFetchData adds env/range automatically for /v2/ paths.
 *
 * @param {boolean} enabled - Set false to defer fetching (e.g. while env is loading).
 */
export function useHomeApps({ enabled = true } = {}) {
  const endpoint = '/v2/data/services';
  const params = { summary: 'true' };
  const { session } = useStytchMemberSession();
  const { selectedEnvironment } = useEnvironment();

  const { data, loading, error } = useFetchData({ endpoint, params, enabled });

  // Key on the stable tenantId, not the session OBJECT — the object gets a new
  // identity on every Stytch heartbeat, which would hand Home a new `apps`
  // array (and re-render every card) every ~4 minutes.
  const tenantId = session?.organization_id;
  const apps = useMemo(() => {
    if (!data) return null;

    const rawApps = (Array.isArray(data.data) ? data.data : []).map(normalizeV2Service);

    // Enhance with demo data
    const enhanced = enhanceHomeResponse(
      { apps: rawApps },
      tenantId,
      selectedEnvironment
    );

    return enhanced.apps;
  }, [data, tenantId, selectedEnvironment]);

  return { apps, loading, error };
}
